import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { DailyUptime, IncidentSeverity, StatusData, StatusService, SystemStatus } from '../status.service';

@Component({
  selector: 'app-status-page',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './status-page.component.html',
  styleUrl: './status-page.component.scss'
})
export class StatusPageComponent implements OnInit, OnDestroy {
  statusData: StatusData | null = null;
  loading = true;
  error: string | null = null;
  private subscription?: Subscription;
  pollInterval = 30000;

  readonly SystemStatus = SystemStatus;
  readonly IncidentSeverity = IncidentSeverity;

  constructor(
    private readonly statusService: StatusService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.loading = true;
    this.subscription = this.statusService.getStatusPolling(this.pollInterval).subscribe({
      next: (data) => {
        this.statusData = data;
        this.loading = false;
        this.error = null;
      },
      error: (err) => {
        this.error = 'Failed to load status information. Please try again later.';
        this.loading = false;
        console.error('Status loading error:', err);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  loadStatus(): void {
    this.loading = true;
    this.statusService.getStatus().subscribe({
      next: (data) => {
        this.statusData = data;
        this.loading = false;
        this.error = null;
      },
      error: (err) => {
        this.error = 'Failed to load status information. Please try again later.';
        this.loading = false;
        console.error('Status loading error:', err);
      }
    });
  }

  getStatusClass(status: SystemStatus): string {
    const statusMap: Record<SystemStatus, string> = {
      [SystemStatus.OPERATIONAL]: 'status-operational',
      [SystemStatus.DEGRADED]: 'status-degraded',
      [SystemStatus.DOWN]: 'status-down',
      [SystemStatus.MAINTENANCE]: 'status-maintenance'
    };
    return statusMap[status] || 'status-unknown';
  }

  getSeverityClass(severity: IncidentSeverity): string {
    const severityMap: Record<IncidentSeverity, string> = {
      [IncidentSeverity.LOW]: 'severity-low',
      [IncidentSeverity.MEDIUM]: 'severity-medium',
      [IncidentSeverity.HIGH]: 'severity-high',
      [IncidentSeverity.CRITICAL]: 'severity-critical'
    };
    return severityMap[severity] || 'severity-unknown';
  }

  getStatusIcon(status: SystemStatus): string {
    const iconMap: Record<SystemStatus, string> = {
      [SystemStatus.OPERATIONAL]: 'OK',
      [SystemStatus.DEGRADED]: '!',
      [SystemStatus.DOWN]: 'X',
      [SystemStatus.MAINTENANCE]: '~'
    };
    return iconMap[status] || '?';
  }

  formatDuration(seconds: number | null | undefined): string {
    if (seconds == null || seconds < 0) {
      return 'Unavailable';
    }

    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    if (minutes < 60) {
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours < 24) {
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }

  formatMilliseconds(value: number | null | undefined): string {
    if (value == null || value < 0) {
      return 'Unavailable';
    }

    if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}s`;
    }

    return `${Math.round(value)} ms`;
  }

  formatMetric(value: number | null | undefined, suffix: string = ''): string {
    if (value == null) {
      return 'Unavailable';
    }

    return `${value.toFixed(3)}${suffix}`;
  }

  formatPercent(value: number | null | undefined): string {
    if (value == null) {
      return 'No data';
    }

    return `${value >= 99.995 ? '100' : value.toFixed(2)}%`;
  }

  getUptimeBarClass(day: DailyUptime): string {
    if (day.uptimePercent == null) {
      return 'bar-nodata';
    }
    if (day.uptimePercent >= 99.95) {
      return 'bar-up';
    }
    if (day.uptimePercent >= 99) {
      return 'bar-minor';
    }
    return 'bar-major';
  }

  getUptimeBarTitle(day: DailyUptime): string {
    if (day.uptimePercent == null) {
      return `${day.date}: no data`;
    }

    const downtime = day.downtimeSeconds ? `, ${this.formatDuration(day.downtimeSeconds)} down` : '';
    const incidents = day.incidents ? `, ${day.incidents} incident${day.incidents > 1 ? 's' : ''}` : '';
    return `${day.date}: ${this.formatPercent(day.uptimePercent)} uptime${downtime}${incidents}`;
  }

  trackByDate(_index: number, day: DailyUptime): string {
    return day.date;
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    }).format(new Date(date));
  }

  formatRelativeTime(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
}
